import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

const COLORS = [
  '#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777',
];

export default function ShiftSchedule() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [shifts, setShifts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empColors, setEmpColors] = useState({});
  const [modal, setModal] = useState(null); // { mode:'create'|'edit', shift, date, employee_id }
  const [form, setForm] = useState({ employee_id: '', start_time: '09:00', end_time: '17:00', note: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, t] = await Promise.all([
        api.get(`/shifts?week_start=${fmtDate(weekStart)}`),
        isAdmin ? api.get('/employees') : Promise.resolve({ data: [] }),
        api.get('/shift-templates').catch(() => ({ data: [] })),
      ]);
      setShifts(s.data);
      setTemplates(t.data || []);
      const emps = e.data.filter ? e.data.filter(e => e.active) : [];
      setEmployees(emps);
      const colors = {};
      emps.forEach((emp, i) => { colors[emp.id] = COLORS[i % COLORS.length]; });
      setEmpColors(colors);
    } catch {}
    setLoading(false);
  }, [weekStart, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const shiftsFor = (empId, date) =>
    shifts.filter(s => s.employee_id === empId && s.date === fmtDate(date));

  // Auto-filled shift from the employee's assigned template on this weekday
  // (only when there's no manual shift for that day — a manual shift overrides).
  const tplById = Object.fromEntries(templates.map(t => [t.id, t]));
  const autoShiftFor = (emp, date) => {
    const t = emp.shift_template_id && tplById[emp.shift_template_id];
    if (!t) return null;
    const days = Array.isArray(t.days) ? t.days : [];
    return days.includes(date.getDay()) ? t : null;
  };

  const openCreate = (empId, date) => {
    setForm({ employee_id: empId, start_time: '09:00', end_time: '17:00', note: '' });
    setError('');
    setModal({ mode: 'create', date, employee_id: empId });
  };

  const openEdit = (shift) => {
    setForm({ employee_id: shift.employee_id, start_time: shift.start_time, end_time: shift.end_time, note: shift.note || '' });
    setError('');
    setModal({ mode: 'edit', shift });
  };

  const save = async () => {
    setError('');
    try {
      if (modal.mode === 'create') {
        await api.post('/shifts', {
          employee_id: form.employee_id,
          date: fmtDate(modal.date),
          start_time: form.start_time,
          end_time: form.end_time,
          note: form.note,
        });
      } else {
        await api.put(`/shifts/${modal.shift.id}`, form);
      }
      setModal(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    }
  };

  const del = async (shiftId) => {
    if (!window.confirm('Delete this shift?')) return;
    await api.delete(`/shifts/${shiftId}`);
    load();
  };

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));

  const displayEmps = isAdmin ? employees : (user?.employee ? [user.employee] : []);

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          {isAdmin ? 'Shift Schedule' : 'My Shifts'}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={prevWeek} style={navBtn}>← Prev</button>
          <span style={{ fontWeight: 600, color: 'var(--text-2)', minWidth: 200, textAlign: 'center' }}>
            {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
          </span>
          <button onClick={nextWeek} style={navBtn}>Next →</button>
          <button onClick={() => setWeekStart(getWeekStart(new Date()))} style={{ ...navBtn, background: '#4f46e5', color: '#fff' }}>Today</button>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--muted)' }}>Loading…</p>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr style={{ background: 'var(--hairline)' }}>
              <th style={th}>Employee</th>
              {weekDates.map((d, i) => (
                <th key={i} style={{ ...th, background: fmtDate(d) === fmtDate(new Date()) ? '#ede9fe' : undefined }}>
                  <div>{DAYS[i]}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>{fmtDate(d).slice(5)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayEmps.map(emp => (
              <tr key={emp.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                <td style={{ ...td, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: empColors[emp.id] || '#4f46e5', flexShrink: 0 }} />
                    {emp.name}
                  </div>
                  {emp.department && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{emp.department}</div>}
                </td>
                {weekDates.map((d, i) => {
                  const dayShifts = shiftsFor(emp.id, d);
                  const auto = dayShifts.length ? null : autoShiftFor(emp, d);
                  return (
                    <td key={i} style={{ ...td, verticalAlign: 'top', minWidth: 100, background: fmtDate(d) === fmtDate(new Date()) ? '#faf5ff' : undefined }}>
                      {auto && (
                        <div style={{ background: `${auto.color}1a`, border: `1px dashed ${auto.color}`, borderRadius: 6, padding: '4px 8px', marginBottom: 4, fontSize: 12 }}
                          title={`Assigned shift: ${auto.name}`}>
                          <div style={{ fontWeight: 600, color: auto.color }}>{auto.start_time}–{auto.end_time}</div>
                          <div style={{ fontSize: 10, color: 'var(--muted)' }}>{auto.name}</div>
                        </div>
                      )}
                      {dayShifts.map(s => (
                        <div key={s.id} style={{ background: empColors[emp.id] || '#4f46e5', color: '#fff', borderRadius: 6, padding: '4px 8px', marginBottom: 4, fontSize: 12 }}>
                          <div style={{ fontWeight: 600 }}>{s.start_time}–{s.end_time}</div>
                          {s.note && <div style={{ opacity: 0.85, fontSize: 11 }}>{s.note}</div>}
                          {isAdmin && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                              <button onClick={() => openEdit(s)} style={shiftBtn}>Edit</button>
                              <button onClick={() => del(s.id)} style={{ ...shiftBtn, background: 'rgba(255,255,255,0.15)' }}>✕</button>
                            </div>
                          )}
                        </div>
                      ))}
                      {isAdmin && (
                        <button onClick={() => openCreate(emp.id, d)} style={addBtn} title="Add shift">+</button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {displayEmps.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--muted)', padding: 32 }}>No employees found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={overlay} onClick={() => setModal(null)}>
          <div style={card} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--text)' }}>
              {modal.mode === 'create' ? 'Add Shift' : 'Edit Shift'}
            </h3>
            {modal.mode === 'create' && (
              <div style={field}>
                <label style={lbl}>Date</label>
                <input value={fmtDate(modal.date)} readOnly style={{ ...inp, background: 'var(--hairline)' }} />
              </div>
            )}
            {isAdmin && modal.mode === 'create' && (
              <div style={field}>
                <label style={lbl}>Employee</label>
                <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} style={inp}>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...field, flex: 1 }}>
                <label style={lbl}>Start</label>
                <input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} style={inp} />
              </div>
              <div style={{ ...field, flex: 1 }}>
                <label style={lbl}>End</label>
                <input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={field}>
              <label style={lbl}>Note (optional)</label>
              <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={inp} placeholder="e.g. Cover shift" />
            </div>
            {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={navBtn}>Cancel</button>
              <button onClick={save} style={{ ...navBtn, background: '#4f46e5', color: '#fff' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text-2)', textAlign: 'center', borderBottom: '2px solid var(--glass-border)' };
const td = { padding: '8px 10px', fontSize: 13, textAlign: 'center' };
const navBtn = { padding: '6px 14px', border: '1px solid var(--glass-border)', borderRadius: 6, background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const addBtn = { width: 24, height: 24, borderRadius: '50%', border: '1px dashed #d1d5db', background: 'transparent', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, lineHeight: '22px', display: 'inline-block' };
const shiftBtn = { background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 10, padding: '2px 5px' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', borderRadius: 12, padding: 24, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const field = { marginBottom: 14 };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--glass-border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
